import type { ReactNode } from 'react';
import { cx } from '../utils.ts';
import './display.css';

/*
 * Spot illustrations for empty states. Line art that takes its colors from theme tokens
 * (--illo-line, --illo-fill, --illo-shade, --accent) so they sit well in both themes.
 */

export type IllustrationName = 'chest' | 'scroll' | 'bulb' | 'board' | 'gallery' | 'ballot' | 'notes' | 'search' | 'dice' | 'cloud' | 'trash' | 'codex';

const L = { fill: 'none', stroke: 'var(--illo-line)', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
const F = { fill: 'var(--illo-fill)', stroke: 'var(--illo-line)', strokeWidth: 2, strokeLinejoin: 'round' } as const;
const A = { fill: 'var(--accent)' } as const;

const sparkle = (x: number, y: number, s = 1) => (
  <path d={`M${x} ${y - 6 * s}l${1.6 * s} ${4.4 * s} ${4.4 * s} ${1.6 * s}-${4.4 * s} ${1.6 * s}-${1.6 * s} ${4.4 * s}-${1.6 * s}-${4.4 * s}-${4.4 * s}-${1.6 * s} ${4.4 * s}-${1.6 * s}z`} {...A} />
);
const shadow = (cx = 80, w = 52) => <ellipse cx={cx} cy={106} rx={w} ry={5} fill="var(--illo-shade)" />;

const ART: Record<IllustrationName, ReactNode> = {
  chest: (
    <>
      {shadow(80, 58)}
      <path d="M40 58 45 30Q80 17 115 30l5 28Z" {...F} />
      <path d="M44 56h72" {...L} />
      <circle cx="62" cy="54" r="7" {...A} />
      <circle cx="77" cy="50" r="8" {...A} />
      <circle cx="93" cy="53" r="7" {...A} />
      <circle cx="106" cy="56" r="5" {...A} />
      <path d="M74 50h6M59 54h6M90 53h6" stroke="var(--accent-fg)" strokeOpacity=".35" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="36" y="58" width="88" height="44" rx="6" {...F} />
      <path d="M58 58v44M102 58v44M36 74h88" {...L} />
      <rect x="73" y="66" width="14" height="16" rx="3" {...A} />
      <circle cx="80" cy="73" r="2" fill="var(--accent-fg)" fillOpacity=".5" />
      {sparkle(30, 34)}
      {sparkle(132, 24, 0.8)}
      {sparkle(128, 52, 0.55)}
    </>
  ),
  scroll: (
    <>
      {shadow(80, 50)}
      <path d="M46 22h66a8 8 0 0 1 8 8v66H54a8 8 0 0 1-8-8Z" {...F} />
      <path d="M38 30a8 8 0 0 1 16 0v58a8 8 0 0 0 8 8h58a8 8 0 0 1-8 8H46a8 8 0 0 1-8-8Z" {...F} />
      <path d="M64 40h40M64 50h44M64 60h32M64 70h40M64 80h24" {...L} strokeWidth={2} opacity=".7" />
      <circle cx="110" cy="84" r="9" {...A} />
      <path d="M106 84l3 3 5-6" stroke="var(--accent-fg)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {sparkle(128, 26, 0.8)}
    </>
  ),
  bulb: (
    <>
      {shadow(80, 30)}
      <circle cx="80" cy="50" r="30" fill="var(--accent-soft)" />
      <path d="M80 22a24 24 0 0 0-14 43.5c3 2.3 5 5.5 5 9.5v3h18v-3c0-4 2-7.2 5-9.5A24 24 0 0 0 80 22Z" {...F} />
      <path d="M71 86h18M73 94h14" {...L} />
      <path d="M74 60l6-9 6 9" {...L} stroke="var(--accent)" />
      <path d="M80 51v20" {...L} stroke="var(--accent)" />
      <path d="M44 42h-8M124 42h-8M52 18l-6-6M108 18l6-6" {...L} stroke="var(--accent)" />
      {sparkle(128, 70, 0.7)}
      {sparkle(34, 72, 0.55)}
    </>
  ),
  board: (
    <>
      {shadow(80, 60)}
      <rect x="22" y="22" width="36" height="78" rx="6" {...F} />
      <rect x="62" y="22" width="36" height="78" rx="6" {...F} />
      <rect x="102" y="22" width="36" height="78" rx="6" {...F} />
      <rect x="27" y="32" width="26" height="14" rx="3" fill="var(--illo-shade)" />
      <rect x="27" y="50" width="26" height="14" rx="3" fill="var(--illo-shade)" />
      <rect x="67" y="32" width="26" height="14" rx="3" {...A} />
      <rect x="107" y="32" width="26" height="14" rx="3" fill="var(--illo-shade)" />
      <rect x="107" y="50" width="26" height="14" rx="3" fill="var(--illo-shade)" />
      <rect x="107" y="68" width="26" height="14" rx="3" fill="var(--illo-shade)" />
      <path d="M31 39h14M71 39h12M111 39h16" stroke="var(--illo-line)" strokeWidth="2" strokeLinecap="round" opacity=".6" />
    </>
  ),
  gallery: (
    <>
      {shadow(80, 54)}
      <rect x="44" y="18" width="76" height="60" rx="6" transform="rotate(8 82 48)" {...F} opacity=".7" />
      <rect x="34" y="28" width="84" height="64" rx="6" {...F} />
      <circle cx="98" cy="46" r="7" {...A} />
      <path d="M40 86l22-26 16 18 10-10 24 18" {...L} />
      {sparkle(128, 28, 0.8)}
    </>
  ),
  ballot: (
    <>
      {shadow(80, 50)}
      <rect x="62" y="14" width="38" height="44" rx="4" transform="rotate(-8 81 36)" {...F} />
      <path d="M71 34l6 6 11-13" stroke="var(--accent)" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" transform="rotate(-8 81 36)" />
      <path d="M36 56h88l-6 46H42Z" {...F} />
      <rect x="30" y="50" width="100" height="12" rx="4" {...F} />
      <path d="M60 56h40" stroke="var(--illo-line)" strokeWidth="3" strokeLinecap="round" />
      <rect x="70" y="74" width="20" height="8" rx="4" {...A} />
      {sparkle(128, 30, 0.7)}
    </>
  ),
  notes: (
    <>
      {shadow(80, 58)}
      <rect x="26" y="34" width="44" height="44" rx="4" transform="rotate(-8 48 56)" fill="var(--note-blue)" />
      <rect x="92" y="30" width="44" height="44" rx="4" transform="rotate(7 114 52)" fill="var(--note-pink)" />
      <rect x="56" y="42" width="48" height="48" rx="4" fill="var(--note-yellow)" />
      <path d="M64 56h30M64 64h26M64 72h18" stroke="#1c1a14" strokeOpacity=".35" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="98" cy="86" r="6" {...A} />
      <circle cx="86" cy="88" r="6" {...A} />
    </>
  ),
  search: (
    <>
      {shadow(80, 40)}
      <circle cx="72" cy="52" r="26" {...F} />
      <circle cx="72" cy="52" r="17" fill="var(--accent-soft)" />
      <path d="M92 72l20 20" {...L} strokeWidth={8} />
      <path d="M92 72l20 20" stroke="var(--accent)" strokeWidth={4} strokeLinecap="round" />
      <path d="M62 44a12 12 0 0 1 12-6" {...L} opacity=".7" />
      {sparkle(122, 32, 0.7)}
    </>
  ),
  dice: (
    <>
      {shadow(80, 40)}
      <path d="M80 14 116 35v42L80 98 44 77V35Z" {...F} />
      <path d="M80 36 97 66H63Z" fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M80 14v22M44 35l19 31M116 35 97 66M44 77l19-11M116 77 97 66M80 98 63 66M80 98l17-32" {...L} opacity=".6" />
      {sparkle(128, 24, 0.8)}
    </>
  ),
  cloud: (
    <>
      {shadow(80, 50)}
      <path d="M48 84a20 20 0 0 1-2-40 26 26 0 0 1 50-6 20 20 0 0 1 20 22 12 12 0 0 1-2 24Z" {...F} />
      <path d="M70 70l10-10 10 10M80 60v22" stroke="var(--accent)" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {sparkle(128, 30, 0.7)}
      {sparkle(34, 36, 0.55)}
    </>
  ),
  trash: (
    <>
      {shadow(80, 36)}
      <path d="M54 38h52l-5 60H59Z" {...F} />
      <path d="M48 38h64M70 38v-8h20v8" {...L} />
      <path d="M72 50v36M88 50v36" {...L} opacity=".6" />
      {sparkle(122, 34, 0.7)}
    </>
  ),
  codex: (
    <>
      {shadow(80, 54)}
      <path d="M30 26c18-4 34-2 50 8v66c-16-10-32-12-50-8Z" {...F} />
      <path d="M130 26c-18-4-34-2-50 8v66c16-10 32-12 50-8Z" {...F} />
      <path d="M40 44c10-2 20-1 30 4M40 56c10-2 20-1 30 4M40 68c10-2 20-1 30 4" {...L} opacity=".6" />
      <circle cx="104" cy="56" r="12" {...A} />
      <path d="M104 48v16M96 56h16" stroke="var(--accent-fg)" strokeOpacity=".45" strokeWidth="2" strokeLinecap="round" />
      {sparkle(134, 22, 0.7)}
    </>
  ),
};

export function Illustration({ name, size = 160, className }: { name: IllustrationName; size?: number; className?: string }) {
  return (
    <svg className={cx('g-illo', className)} width={size} height={(size * 120) / 160} viewBox="0 0 160 120" aria-hidden="true">
      {ART[name]}
    </svg>
  );
}
